import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Datepicker from 'components/ReusableComponents/Datepicker'
import ScheduledIcon from 'components/ReusableComponents/Icons/Scheduled'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import ScheduleDescription from 'features/Announcements/AnnouncementConfirmation/ScheduleDescription'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import { inPropertyAudiences } from '../constants'
import styles from './AnnouncementConfirmation.module.scss'

const AnnouncementConfirmation = ({
  handleConfirm,
  handleCancel,
  audience,
  totalEligibleForText,
  announcementType,
  closeEditTextDialog,
  defaultIsRecurring,
  isEditing,
  defaultRepeatKind = 'Day',
  defaultDaysOfTheWeek = [],
  defaultStartDate,
  defaultEndDate
}) => {
  const { openUnlockServiceDialog, hasUnlimitedService } = useUnlockService()

  const audienceData = inPropertyAudiences[audience]
  const Icon = audienceData?.icon
  const title = audienceData?.name
  const timeZoneId = useSelector(s => s.hotelDetails.ianaTimeZoneId)

  const [date, setDate] = useState(
    moment(moment().tz(timeZoneId).format('YYYY-MM-DD hh:mm A'))
  )

  const [isRecurring, setIsRecurring] = useState(defaultIsRecurring)
  const [repeatKind, setRepeatKind] = useState(defaultRepeatKind)

  const [daysOfTheWeek, setDaysOfTheWeek] = useState(defaultDaysOfTheWeek)

  const { addToast } = useToasts()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [endDate, setEndDate] = useState()

  function handleSubmit() {
    if (!hasUnlimitedService) {
      openUnlockServiceDialog(features.UnlimitedFeatures)
      closeEditTextDialog()
      return
    }
    setIsSubmitting(true)

    if (announcementType === 'schedule') {
      if (moment(date).isAfter(moment().tz(timeZoneId).format('YYYY-MM-DD hh:mm A'))) {
        const formattedDate = moment(date).format('YYYY-MM-DD hh:mm A')
        handleConfirm({
          date: formattedDate,
          isRecurring,
          repeatKind,
          daysOfTheWeek,
          endDate
        })
      } else {
        addToast('Please select a date & time in the future.', {
          appearance: 'error',
          autoDismiss: true
        })
        setIsSubmitting(false)
      }
    } else {
      handleConfirm({ repeatCount: 0 })
    }
  }

  useEffect(() => {
    if (isEditing && defaultStartDate) {
      setDate(moment(defaultStartDate).format('YYYY-MM-DD hh:mm A'))
    }

    if (isEditing && defaultEndDate) {
      setEndDate(moment(defaultEndDate).format('YYYY-MM-DD'))
    }

    if (!isEditing && announcementType === 'schedule') {
      const initialDate = moment()
        .tz(timeZoneId)
        .add(2, 'minutes')
        .format('YYYY-MM-DD hh:mm A')
      setDate(initialDate)
    }
  }, [announcementType, defaultStartDate, defaultEndDate, isEditing, timeZoneId])

  function handleChangeHour(e) {
    const inputValue = e.target.value

    let hour = inputValue

    if (inputValue > 12) hour = 12

    if (inputValue < 1) hour = 1

    const fullDate = moment(date).format('YYYY-MM-DD')
    const minute = moment(date).format('mm')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangeMinute(e) {
    const inputValue = e.target.value

    let minute = Number(inputValue)

    if (inputValue > 59) minute = 59

    if (inputValue < 0) minute = 0

    if (inputValue < 10) minute = `0${minute}`

    const fullDate = moment(date).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangePeriod(e) {
    let am_pm = e.target.value

    const fullDate = moment(date).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const minute = moment(date).format('mm')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangeDate(newDate) {
    const fullDate = moment(newDate).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const minute = moment(date).format('mm')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleSelectDayOfTheWeek(day) {
    if (daysOfTheWeek.includes(day)) {
      setDaysOfTheWeek(daysOfTheWeek.filter(d => d !== day))
    } else {
      setDaysOfTheWeek([...daysOfTheWeek, day])
    }
  }

  const DayButton = ({ day }) => (
    <button
      className={`${styles.dayButton} ${
        daysOfTheWeek.includes(day) ? styles.dayButtonSelected : ''
      }`}
      onClick={() => handleSelectDayOfTheWeek(day)}
    >
      {day.charAt(0)}
    </button>
  )

  return (
    <ConfirmationDialog
      title={
        announcementType === 'schedule' ? 'Schedule announcement' : 'Confirm announcement'
      }
      onConfirm={() => handleSubmit()}
      onClose={handleCancel}
      negativeMessage="Cancel"
      positiveMessage={isEditing ? 'Update' : 'Confirm'}
      contentPadding={0}
      isButtonDisabled={isSubmitting}
    >
      <div className={styles.summary} id="announcement-estimation">
        <div className={styles.row}>
          <div className={styles.audience}>
            <Icon className={styles.icon} />
            <div>
              <p className={styles.audienceName}>{title}</p>
              <p className={styles.audienceSize}>
                {announcementType === 'schedule' && <>Approx. </>}
                {totalEligibleForText} guests
              </p>
            </div>
          </div>
        </div>
      </div>
      {announcementType === 'schedule' ? (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.dateWrapper}>
              <div className={styles.dateholder}>
                <div className={styles.date}>
                  <p className={styles.label}>{isRecurring ? 'Start date' : 'Date'}</p>
                  <Datepicker
                    selected={moment(date).toDate()}
                    className={styles.datepicker}
                    onChange={handleChangeDate}
                    minDate={moment().tz(timeZoneId).toDate()}
                  />
                </div>
              </div>
              <div className={styles.timeHolder}>
                <p className={styles.label}>{isRecurring ? 'Start time' : 'Time'}</p>
                <div className={styles.time}>
                  <TextInput
                    type="number"
                    min={1}
                    max={12}
                    value={moment(date).format('h')}
                    onChange={handleChangeHour}
                  />
                  <TextInput
                    type="number"
                    min={0}
                    max={60}
                    value={moment(date).format('mm')}
                    onChange={handleChangeMinute}
                  />
                  <Dropdown
                    options={{ AM: 'AM', PM: 'PM' }}
                    value={moment(date).format('A')}
                    onChange={handleChangePeriod}
                  />
                </div>
              </div>
            </div>
            <div className={styles.dateholder}></div>
          </div>
          <div className={styles.repeatAnnouncementControls}>
            <label className={styles.recurringToggleHolder}>
              {!isEditing && (
                <Toggle
                  checked={isRecurring}
                  handleChange={e => setIsRecurring(e.target.checked)}
                />
              )}
              Repeat announcement <div className={styles.newPill}>New</div>
            </label>
            {isRecurring && (
              <div className={styles.repeatDropdownHolder}>
                <div className={styles.repeatCountHolder}>
                  <span className={styles.repeatLabel}>Repeat every</span>
                  <Dropdown
                    className={styles.repeatDropdown}
                    defaultValue={'Repeat 1 time'}
                    isSortedAlphabetically={false}
                    options={{
                      Day: 'Day',
                      Week: 'Week'
                    }}
                    value={repeatKind}
                    onChange={e => setRepeatKind(e.target.value)}
                  />
                </div>
                {repeatKind === 'Week' && (
                  <div className={styles.repeatOnDayHolder}>
                    <span className={styles.repeatLabel}>Repeat on</span>
                    <div className={styles.daysOfTheWeek}>
                      <DayButton day="Sunday" />
                      <DayButton day="Monday" />
                      <DayButton day="Tuesday" />
                      <DayButton day="Wednesday" />
                      <DayButton day="Thursday" />
                      <DayButton day="Friday" />
                      <DayButton day="Saturday" />
                    </div>
                  </div>
                )}
                <div className={styles.repeatOnDayHolder}>
                  <p className={styles.repeatLabel}>End date</p>
                  <Datepicker
                    selected={endDate ? moment(endDate).toDate() : null}
                    className={styles.endDatepicker}
                    placeholderText="No end date"
                    onChange={endDate =>
                      endDate
                        ? setEndDate(moment(endDate).format('YYYY-MM-DD'))
                        : setEndDate(null)
                    }
                    minDate={moment(date).toDate()}
                    isClearable
                  />
                </div>
                <div className={styles.scheduleDescriptionHolder}>
                  <ScheduledIcon className={styles.scheduleIcon} />
                  <ScheduleDescription
                    DaysOfWeeks={daysOfTheWeek}
                    RepeatKind={repeatKind}
                    StartDate={moment(date).format('YYYY-MM-DD hh:mm A')}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className={styles.content}>
            <p className={styles.contentText}>Texts will be sent right now.</p>
          </div>
        </div>
      )}
    </ConfirmationDialog>
  )
}

export default AnnouncementConfirmation
